/**
 * What a LOST concurrency race resolves to — and, unlike mobile, **what the trainer would be giving
 * up by adopting the winner**, named node by node.
 *
 * ── Why there are only ever two outcomes ─────────────────────────────────────────────────────
 * A save submits the WHOLE tree guarded by an opaque `baseVersion` (document-model ADR §4), so the
 * loser of a race gets a 409. Refreshing only the token and retrying is consented last-write-wins:
 * the retry re-sends the tree as it stood BEFORE the other edit landed, so the winner's edit
 * disappears and nobody is told. With stable day and exercise tokens on the wire it is worse than
 * that — a stale tree RESURRECTS rows the winning edit deleted, because the backend recognises their
 * identity and rebuilds them.
 *
 * That is a property of the save protocol, not a missing feature, and it applies to this app exactly
 * as it applies to the phone. So the recovery is: **adopt the winning tree, or keep editing under a
 * token that will conflict again.** There is no "save mine anyway" and this module offers no third
 * value that could become one. An automatic three-way merge is declined for the same reason ADR §6
 * declines patch persistence: an Immer patch addresses its target by array index, so replaying one
 * against a tree the winner has since reshaped succeeds against whatever now occupies that index.
 *
 * ── The diff runs against the BASELINE, not against the edited draft ─────────────────────────
 * This is the whole design, and getting it backwards produces a dialog that is confidently wrong.
 * The question the trainer needs answered is "what did the OTHER person change?" — which is
 * `winning` against the tree as it was LOADED. Diffing against the edited draft answers "how do my
 * unsaved edits differ from the server?", which folds the trainer's own work into the list and
 * reports their rename as somebody else's change.
 *
 * The store keeps the pristine baseline for exactly this (ADR §5), so it costs nothing to ask the
 * right question. `theWinningTreeIsDiffedAgainstTheLoadBaselineNotAgainstTheEditedDraft` pins it.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/saveConflict.ts`, and extended ──
 * Mobile reports tree COUNTS plus a `differs` fingerprint — "3 phases became 4" — and its own header
 * says why: "slot tokens now give days and exercises stable identity across a save, so a row-level
 * 'this exercise was removed' has become expressible — it is simply not built yet". ADR §4 commits
 * this app to building it, and this is that. What is kept verbatim is the part that decides WHETHER
 * anything changed:
 *
 * · **The fingerprint ignores every identity and provenance key** — `slotToken`, `serverId`,
 *   `serverOrder` — and the whole `live` context, which is never trainer-authored. Comparing any of
 *   them would report a reload the trainer cannot see as a change. `serverOrder` in particular: the
 *   AUTHORED order is the array's own, which the walk already covers, so comparing where a day used
 *   to sit would call two identically-ordered trees different whenever one of them reached that
 *   order by being reordered.
 * · **`tempId` is not in the exclusion list**, because this app's tree has no such field
 *   (`planDraft.types.ts` note 1). Its absence is the change, not an oversight.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. Every name below is DATA — a phase,
 * day or exercise name the trainer typed — and the dialog that renders them supplies its own copy.
 */

import type { DayDraft, ExerciseDraft, PhaseDraft, PlanDraft } from './planDraft.types';

/**
 * What happened to one node between the tree that was loaded and the tree that won.
 *
 * `reordered` is the coarse one and exists because the fine-grained walk has a blind spot: it
 * matches nodes by durable identity, so a pure REORDER — the same days, the same exercises, the same
 * every authored field, in a different sequence — produces no `added`, no `removed` and no `edited`
 * line at all. The fingerprint still differs, because array order is part of it. See
 * `summariseConflict` for why an empty list beside `changed: true` was a dialog with nothing in it.
 */
export type ConflictChangeKind = 'added' | 'removed' | 'edited' | 'reordered';

/** Which level of the tree a change sits at. Ordered coarse to fine when a summary is rendered. */
export type ConflictNodeKind = 'plan' | 'phase' | 'day' | 'exercise';

/**
 * One named difference. `name` is DATA — whatever the trainer called the node — and is empty for an
 * unnamed one; a renderer decides what to say then, because "unnamed day" is copy and copy is not
 * this module's business.
 */
export interface ConflictChange {
  readonly node: ConflictNodeKind;
  readonly kind: ConflictChangeKind;
  readonly name: string;
}

/** The answer a 409 dialog is built from. */
export interface ConflictSummary {
  /**
   * Did anything the trainer could SEE change? False means the winning tree is authored-identical to
   * the one that was loaded, and the caller may adopt it silently — there is nothing to lose and
   * nothing to check. A prompt in that case asks the trainer to arbitrate between two identical
   * trees, which they cannot do and should not be asked to.
   */
  readonly changed: boolean;
  /**
   * Every named difference, coarse to fine. Empty **only** when `changed` is false.
   *
   * That guarantee is enforced rather than argued: `summariseConflict` ends by falling back to a
   * plan-level line when the fine-grained walk found nothing, so a caller may render this list
   * unconditionally whenever `changed` is true and can never produce a dialog whose body is blank.
   * An earlier revision asserted the same thing from the shape of the walk and was WRONG — a
   * concurrent reorder reaches exactly that state.
   */
  readonly changes: readonly ConflictChange[];
}

/**
 * Keys that carry identity or provenance rather than authored content. Excluded from every
 * fingerprint, at every level.
 *
 * **`serverCycles`, `liveClosedCycles` and `coverImageUrl` joined on 15 August 2026, and leaving any
 * of them in would have made the conflict dialog lie.** All three are the SERVER's answer rather than
 * the trainer's: two are the cycles it resolved a row into and the weeks a client has closed, the
 * third is the URL it derived from `coverId`. A concurrent save by another trainer changes the first
 * two on rows nobody edited — every row, on any phase-length change — so a diff that read them would
 * report "the whole plan changed" whenever the real conflict was one renamed day.
 *
 * The AUTHORED window — `activationCycle`, `retirementCycle`, `excludedCycles` — is deliberately NOT
 * here. It is exactly what a freeform edit changes, so a race over which weeks a day runs in has to
 * be nameable.
 */
const NON_AUTHORED_KEYS: ReadonlySet<string> = new Set([
  'slotToken',
  // A set's render identity, minted per load and never sent. Without it here, a reload that changed
  // nothing fingerprints as a change — every set arrives with a new id — and the 409 recovery
  // reports a conflict against the trainer's own unedited tree.
  'localId',
  'serverId',
  'serverOrder',
  'serverCycles',
  'liveClosedCycles',
  // What the CLIENT trained, read back on every load and emitted by nothing. It moves whenever the
  // athlete finishes a session — on rows no trainer has touched — so a fingerprint reading it would
  // report "somebody changed the whole plan" every time the client went to the gym.
  'executions',
  'coverImageUrl',
  'live',
]);

/**
 * A stable string for anything the trainer authored, with identity stripped.
 *
 * `JSON.stringify`'s replacer walks every nested key, so one set covers all four levels. Key ORDER
 * is not a hazard here even though `stringify` preserves it: both trees come out of the same mapper
 * (`responseToDraft`), which writes its object literals in one fixed order.
 */
function authoredFingerprint(value: unknown): string {
  return JSON.stringify(value, (key, nested: unknown) =>
    NON_AUTHORED_KEYS.has(key) ? undefined : nested,
  );
}

/** The plan's own fields, without the phase tree — so a plan-level edit is reported as its own change. */
function planFieldsFingerprint(draft: PlanDraft): string {
  const { phases: _phases, live: _live, ...fields } = draft;
  return authoredFingerprint(fields);
}

/**
 * Match nodes across the two trees by their durable identity, and report what happened to each.
 *
 * Phases match on `serverId`, days and exercises on `slotToken` — the three mechanisms ADR §1 lists,
 * used for exactly what they are for. A phase with no `serverId` exists nowhere server-side, so it
 * cannot appear in a tree the server just returned; such a phase in the baseline is skipped rather
 * than reported as removed.
 */
function diffNodes<T>(
  before: readonly T[],
  after: readonly T[],
  identity: (node: T) => string | null,
  name: (node: T) => string,
  node: ConflictNodeKind,
): ConflictChange[] {
  const changes: ConflictChange[] = [];
  const afterByKey = new Map<string, T>();
  for (const item of after) {
    const key = identity(item);
    if (key !== null) afterByKey.set(key, item);
  }

  const seen = new Set<string>();
  for (const item of before) {
    const key = identity(item);
    if (key === null) continue;
    seen.add(key);
    const counterpart = afterByKey.get(key);
    if (counterpart === undefined) {
      changes.push({ node, kind: 'removed', name: name(item) });
      continue;
    }
    if (authoredFingerprint(item) !== authoredFingerprint(counterpart)) {
      changes.push({ node, kind: 'edited', name: name(counterpart) });
    }
  }

  for (const item of after) {
    const key = identity(item);
    if (key === null || seen.has(key)) continue;
    changes.push({ node, kind: 'added', name: name(item) });
  }

  return changes;
}

function allDays(draft: PlanDraft): DayDraft[] {
  return draft.phases.flatMap((phase) => [...phase.days]);
}

function allExercises(draft: PlanDraft): ExerciseDraft[] {
  return allDays(draft).flatMap((day) => [...day.exercises]);
}

/**
 * A phase's own fingerprint must not include its days, or every day edit would also report the phase
 * as edited and the dialog would say the same thing twice at two levels.
 */
function phaseWithoutDays(phase: PhaseDraft): Omit<PhaseDraft, 'days'> {
  const { days: _days, ...rest } = phase;
  return rest;
}

function dayWithoutExercises(day: DayDraft): Omit<DayDraft, 'exercises'> {
  const { exercises: _exercises, ...rest } = day;
  return rest;
}

/**
 * Diff the tree that WON against the tree that was LOADED, and name what moved.
 *
 * The nesting is flattened deliberately: days are compared across the whole plan rather than within
 * their phase, because a day's `slotToken` is plan-unique and a day MOVED between phases is one day
 * that changed rather than one removed and one added. Mobile's `ServerPlanStructure` nests for the
 * opposite reason — it answers "was this phase deleted, taking its days with it?", where conflating
 * the two sends a phase deletion through a preview round trip that can only come back "nothing
 * moved". Both readings are right for their question; this one is the trainer-facing list.
 *
 * `changed` is the fingerprint answer rather than "is the list non-empty", so a difference this
 * function has no node kind for still reports as a change. The two cannot disagree in the direction
 * that matters: a named change always implies a fingerprint difference.
 *
 * ── The empty-list case is REAL, and it is a concurrent reorder ───────────────────────────────
 * The reverse direction — a fingerprint difference with no named change — was assumed impossible
 * until 2026-08-02, on the argument that the `plan` entry covers anything the node kinds miss. It
 * does not. `planFieldsFingerprint` strips `phases` entirely, and the identity walk matches nodes by
 * `slotToken` and `serverId` rather than by position, so a tree in which the other trainer only
 * MOVED things — dragged Wednesday's workout to Friday, reordered two exercises — has:
 *
 * · a different whole-tree fingerprint (array order is part of `JSON.stringify`), so `changed` is true;
 * · identical plan-level fields, so no `plan` line;
 * · every node matched, present on both sides, and authored-identical, so no `added`, `removed` or
 *   `edited` line either.
 *
 * The dialog then opened with a heading, two buttons and an empty list, asking the trainer to
 * discard their work over changes it declined to name. So the walk ends with a fallback: if nothing
 * finer was found, say the coarse true thing — the plan's order changed. It is stated as its own
 * `reordered` kind rather than reused as `plan`/`edited`, because "zmieniono dane programu" would be
 * a false statement about the plan's own fields, and a conflict dialog is the last place to guess.
 */
export function summariseConflict(baseline: PlanDraft, winning: PlanDraft): ConflictSummary {
  const changed = authoredFingerprint(baseline) !== authoredFingerprint(winning);
  if (!changed) return { changed: false, changes: [] };

  const changes: ConflictChange[] = [];

  if (planFieldsFingerprint(baseline) !== planFieldsFingerprint(winning)) {
    changes.push({ node: 'plan', kind: 'edited', name: winning.name });
  }

  changes.push(
    ...diffNodes(
      baseline.phases.map(phaseWithoutDays),
      winning.phases.map(phaseWithoutDays),
      (phase) => (phase.serverId === null ? null : String(phase.serverId)),
      (phase) => phase.name,
      'phase',
    ),
  );
  changes.push(
    ...diffNodes(
      allDays(baseline).map(dayWithoutExercises),
      allDays(winning).map(dayWithoutExercises),
      (day) => day.slotToken,
      (day) => day.sessionName ?? day.name ?? '',
      'day',
    ),
  );
  changes.push(
    ...diffNodes(
      allExercises(baseline),
      allExercises(winning),
      (exercise) => exercise.slotToken,
      (exercise) => exercise.name,
      'exercise',
    ),
  );

  // The tree differs but nothing above could name what moved — see the header. The only authored
  // difference the walk cannot see is SEQUENCE, so that is what this says, and it says it about the
  // plan because a reorder has no single node to blame.
  if (changes.length === 0) {
    changes.push({ node: 'plan', kind: 'reordered', name: winning.name });
  }

  return { changed: true, changes };
}
